import React from 'react';
import CapeTownSkyline from './CapeTownSkyline.mjs';

/**
 * The one page frame every route uses, so backgrounds and page headers are the
 * same everywhere instead of each page choosing its own (full photo, faint
 * photo, plain gray - three treatments had grown up side by side).
 *
 * variant="app" (default) - signed-in work pages:
 *   a Cape Town photo band under the navbar, cardinal scrim, holding the page
 *   title; the content column starts below the band and runs on
 *   a calm neutral surface (.page-shell / .page-band in index.css).
 * variant="entry" - sign-in / sign-up / profile completion:
 *   the full photo behind a centred card. Pass the card as children.
 *
 * Props (app):
 *   title      page title (Title Case); optional (guards' loading states omit it)
 *   subtitle   one line under the title; string or node
 *   icon       optional react-icons / svg component shown beside the title
 *   actions    optional node at the band's right edge (e.g. a Back or Refresh
 *              button); stacks under the title on phones
 *   width      content column: '7xl' (default, dashboards/tables),
 *              'wide' (the monthly report sheet), '5xl' (medium single-purpose
 *              pages), '3xl' (profile, single cards)
 *   className  extra classes for the <main> content region
 *   photo      false drops the Cape Town photo and keeps a plain cardinal band
 *              (claim forms, the monthly report under review)
 *   align      'center' (default) centres the title, subtitle and actions in
 *              the band; 'left' keeps title left, actions right (home page)
 *   centerContent  true puts the content in the middle of the space under the
 *              band, for pages that are a set of cards to choose from (home,
 *              the role dashboards, PTAE regions) - at the top they read as
 *              stuck under the band with the page empty below (2026-10-21).
 *              From the sm breakpoint up only: on a phone the cards start
 *              right under the band, where they are seen first and in reach,
 *              instead of being pushed down by a gap on a short screen.
 *              Content taller than the space still starts under the band.
 *   skyline    true draws Cape Town's skyline along the foot of the page
 *              (CapeTownSkyline), the band's own view in outline, so the grey
 *              around a page of cards does not read as empty (2026-10-21).
 *              With centerContent the cards centre in the space between the
 *              band and the skyline.
 *
 * The side padding sits OUTSIDE the max width, so the column is as wide as the
 * pages' old "px-4 > max-w-7xl" containers (1280px, not 1232px).
 *
 * Modals, overlays and toasts may render inside: the content region is
 * positioned but deliberately NOT a stacking context (no z-index), so their
 * own z-index still lifts them above the fixed navbar.
 *
 * The band is decorative and never printed (.no-print); pages that print keep
 * their own print-only title.
 */
const WIDTHS = {
  wide: 'max-w-[1500px]',
  '7xl': 'max-w-7xl',
  '5xl': 'max-w-5xl',
  '3xl': 'max-w-3xl',
};

const PageShell = ({
  variant = 'app',
  title,
  subtitle,
  icon: Icon,
  actions,
  width = '7xl',
  className = '',
  // false = the cardinal band without the Cape Town photo (.page-band-plain).
  photo = true,
  // 'center' (default): title, subtitle and actions centred in the band, with
  // or without the photo. 'left': title left, actions at the right edge -
  // the home page only, where the band is a greeting beside the cards.
  align = 'center',
  centerContent = false,
  skyline = false,
  children,
}) => {
  if (variant === 'entry') {
    return (
      <div className="page-shell-entry min-h-screen flex items-center justify-center px-4 pt-20 pb-10">
        {children}
      </div>
    );
  }

  const maxW = WIDTHS[width] || WIDTHS['7xl'];
  const centred = align !== 'left';

  return (
    <div className={`page-shell min-h-screen${skyline
      ? ' flex flex-col'
      : (centerContent ? ' sm:flex sm:flex-col' : '')}`}>
      <header className={`page-band${photo ? '' : ' page-band-plain'}${centred ? ' page-band-centred' : ''} no-print px-4 sm:px-6${centerContent ? ' shrink-0' : ''}`}>
        <div className={`${maxW} mx-auto flex flex-col gap-3 sm:gap-6 ${centred
          ? 'items-center text-center'
          : 'sm:flex-row sm:items-end sm:justify-between'}`}>
          <div className={`min-w-0 flex items-start gap-3${centred ? ' justify-center' : ''}`}>
            {Icon && (
              <span className="shrink-0 mt-1 sm:mt-1.5 w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-white/15 text-white flex items-center justify-center" aria-hidden="true">
                <Icon className="w-5 h-5" />
              </span>
            )}
            <div className="min-w-0">
              {title && <h1 className="page-band-title">{title}</h1>}
              {subtitle && <p className="page-band-subtitle">{subtitle}</p>}
            </div>
          </div>
          {actions && (
            <div className="shrink-0 flex flex-wrap items-center gap-2">{actions}</div>
          )}
        </div>
      </header>

      {/* Centred (sm and up): the space under the band is shared evenly above
          and below the content - the bottom padding matches the body's top
          margin. A phone keeps the ordinary top-aligned layout. */}
      <main className={`page-body relative px-4 sm:px-6 ${centerContent
        ? 'pb-12 sm:flex-1 sm:flex sm:flex-col sm:justify-center sm:pb-8'
        : 'pb-12'} ${className}`}>
        <div className={`${maxW} mx-auto${centerContent ? ' w-full' : ''}`}>
          {children}
        </div>
      </main>

      {skyline && <CapeTownSkyline />}
    </div>
  );
};

export default PageShell;
